import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { CalendarCheck, Plus, Edit, Trash2, RefreshCw, AlertTriangle, Download, Upload, List, MoreHorizontal, ClipboardEdit } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["Present", "Absent", "Half-day", "Leave"];

const HRAttendance = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selMonth, setSelMonth] = useState(new Date().getMonth() + 1);
  const [selYear, setSelYear] = useState(new Date().getFullYear());
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ employee_name: "", date: new Date().toISOString().split("T")[0], status: "Present", check_in: "09:00", check_out: "17:30", notes: "" });
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkMode, setBulkMode] = useState("date");
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().split("T")[0]);
  const [bulkEmployee, setBulkEmployee] = useState("");
  const [bulkRows, setBulkRows] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);

  const fetchData = useCallback(async () => {
    const [attRes, empRes] = await Promise.all([
      axios.get(`${API}/hr/attendance`, { params: { month: selMonth, year: selYear } }),
      axios.get(`${API}/hr/employees`),
    ]);
    setRecords(attRes.data);
    setEmployees(empRes.data);
  }, [selMonth, selYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!form.employee_name || !form.date) { toast.error("Employee and date required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/hr/attendance/${editing.id}`, form);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/hr/attendance`, { ...form, source: "manual" });
        toast.success("Attendance marked");
      }
      setShowDialog(false); setEditing(null); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    await axios.delete(`${API}/hr/attendance/${id}`);
    toast.success("Deleted"); fetchData();
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await axios.post(`${API}/hr/attendance/sync-timesheet`, { date: today });
      toast.success(`Synced: ${res.data.synced} new, ${res.data.updated || 0} updated`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const openBulkEdit = (mode) => {
    setBulkMode(mode);
    setShowBulkEdit(true);
    if (mode === "date") {
      const dateRecords = records.filter(r => r.date === bulkDate);
      const rows = employees.map(emp => {
        const existing = dateRecords.find(r => r.employee_name === emp.name);
        return {
          id: existing?.id || "", employee_name: emp.name, date: bulkDate,
          status: existing?.status || "", check_in: existing?.check_in || "",
          check_out: existing?.check_out || "", overtime_hours: String(existing?.overtime_hours || ""),
          notes: existing?.notes || "",
        };
      });
      setBulkRows(rows);
    } else if (mode === "employee" && bulkEmployee) {
      const daysInMonth = new Date(selYear, selMonth, 0).getDate();
      const empRecords = records.filter(r => r.employee_name === bulkEmployee);
      const rows = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${selYear}-${String(selMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        const existing = empRecords.find(r => r.date === dateStr);
        rows.push({
          id: existing?.id || "", employee_name: bulkEmployee, date: dateStr,
          status: existing?.status || "", check_in: existing?.check_in || "",
          check_out: existing?.check_out || "", overtime_hours: String(existing?.overtime_hours || ""),
          notes: existing?.notes || "",
        });
      }
      setBulkRows(rows);
    }
  };

  const updateBulkRow = (idx, field, val) => {
    setBulkRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };

  const handleBulkSave = async () => {
    const validRows = bulkRows.filter(r => r.employee_name && r.date && r.status);
    if (validRows.length === 0) { toast.error("No rows with status to save"); return; }
    setSavingBulk(true);
    try {
      const res = await axios.post(`${API}/hr/attendance/bulk-upload`, { rows: validRows });
      toast.success(`Saved: ${res.data.created} created, ${res.data.updated} updated`);
      setShowBulkEdit(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSavingBulk(false); }
  };


  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/hr/attendance/download-template`, {
        params: { month: selMonth, year: selYear },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${months[selMonth - 1]}_${selYear}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill in P/A/H/L and OT hours, then upload");
    } catch { toast.error("Download failed"); }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const res = await axios.post(`${API}/hr/attendance/upload-template`, {
          csv: evt.target.result,
          year: selYear,
        });
        toast.success(`Upload complete: ${res.data.created} created, ${res.data.updated} updated`);
        fetchData();
      } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
      finally { setUploading(false); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const statusBadge = (s) => ({
    "Present": "bg-green-900/40 text-green-400",
    "Absent": "bg-red-900/40 text-red-400",
    "Half-day": "bg-amber-900/40 text-amber-400",
    "Leave": "bg-blue-900/40 text-blue-400",
  }[s] || "bg-gray-800 text-gray-400");

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // Group by employee for summary
  const empSummary = {};
  records.forEach(r => {
    if (!empSummary[r.employee_name]) empSummary[r.employee_name] = { present: 0, absent: 0, half: 0, leave: 0, overtime: 0 };
    const s = empSummary[r.employee_name];
    if (r.status === "Present") s.present++;
    else if (r.status === "Absent") s.absent++;
    else if (r.status === "Half-day") s.half++;
    else if (r.status === "Leave") s.leave++;
    s.overtime += r.overtime_hours || 0;
  });

  return (
    <div className="space-y-4" data-testid="hr-attendance-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-teal-400 flex items-center gap-2"><CalendarCheck className="w-5 h-5" /> Attendance</h2>
          <p className="text-gray-400 text-sm">Shift: 9:00 AM - 5:30 PM</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={() => navigate("/hr/manual-attendance")}
            className="bg-teal-600 hover:bg-teal-700 text-white shadow-md" data-testid="manual-attendance-btn">
            <ClipboardEdit className="w-4 h-4 mr-1.5" /> Manual Attendance
          </Button>
          <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing}
            className="border-cyan-600 text-cyan-300 hover:bg-cyan-900/30" data-testid="sync-timesheet-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing..." : "Sync Timesheet"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowBulkEdit(true)}
            className="border-amber-600 text-amber-300 hover:bg-amber-900/30" data-testid="bulk-edit-btn">
            <List className="w-4 h-4 mr-1" /> Bulk Edit
          </Button>
          <Button size="sm" onClick={() => { setEditing(null); setForm({ employee_name: "", date: new Date().toISOString().split("T")[0], status: "Present", check_in: "09:00", check_out: "17:30", notes: "" }); setShowDialog(true); }}
            className="bg-gray-700 hover:bg-gray-600" data-testid="add-attendance-btn">
            <Plus className="w-4 h-4 mr-1" /> Single Entry
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="attendance-more-btn">
                <MoreHorizontal className="w-4 h-4 mr-1" /> More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-gray-900 border-gray-700 text-white">
              <DropdownMenuItem onClick={handleDownloadTemplate} data-testid="more-download-template" className="hover:bg-gray-800 cursor-pointer">
                <Download className="w-4 h-4 mr-2 text-emerald-400" /> Download CSV Template
              </DropdownMenuItem>
              <DropdownMenuItem asChild data-testid="more-upload-csv" className="hover:bg-gray-800 cursor-pointer">
                <label className="cursor-pointer flex items-center w-full">
                  <Upload className="w-4 h-4 mr-2 text-purple-400" /> {uploading ? "Uploading..." : "Upload Filled CSV"}
                  <input type="file" accept=".csv" onChange={handleUploadCSV} className="hidden" disabled={uploading} />
                </label>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-700" />
              <DropdownMenuItem onClick={() => navigate("/hr/manual-attendance")} className="hover:bg-gray-800 cursor-pointer">
                <ClipboardEdit className="w-4 h-4 mr-2 text-teal-400" /> Open Manual Page (recommended)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Month/Year selector */}
      <div className="flex gap-3 items-center">
        <Select value={String(selMonth)} onValueChange={v => setSelMonth(parseInt(v))}>
          <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-32"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-600">{months.map((m, i) => <SelectItem key={i} value={String(i + 1)} className="text-white">{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(selYear)} onValueChange={v => setSelYear(parseInt(v))}>
          <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-24"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-600">{[2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)} className="text-white">{y}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-gray-400 text-sm">{records.length} records</span>
      </div>

      {/* How-to info */}
      <Card className="bg-teal-950/20 border-teal-500/20">
        <CardContent className="p-3 text-sm text-gray-300">
          <span className="text-teal-400 font-medium">Recommended: </span>
          Click <button onClick={() => navigate("/hr/manual-attendance")} className="text-teal-300 underline hover:text-teal-200 font-medium" data-testid="hint-manual-link">Manual Attendance</button> for the easiest entry — daily list with one-tap status buttons, monthly grid, smart defaults (mark all Present / copy yesterday / Sunday-as-Leave) and absence reasons.
          <span className="block text-xs text-gray-500 mt-1">CSV download/upload is still available under the More menu for legacy workflows.</span>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {Object.keys(empSummary).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {Object.entries(empSummary).map(([name, s]) => (
            <Card key={name} className="bg-gray-900/30 border-gray-700">
              <CardContent className="p-3">
                <div className="text-white text-xs font-medium truncate mb-1">{name}</div>
                <div className="flex gap-2 text-xs">
                  <span className="text-green-400">{s.present}P</span>
                  <span className="text-red-400">{s.absent}A</span>
                  <span className="text-amber-400">{s.half}H</span>
                  {s.overtime > 0 && <span className="text-cyan-400">{s.overtime.toFixed(1)}OT</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Attendance Table */}
      <Card className="bg-gray-900/30 border-gray-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400">Date</TableHead>
                <TableHead className="text-gray-400">Employee</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400">Check In</TableHead>
                <TableHead className="text-gray-400">Check Out</TableHead>
                <TableHead className="text-gray-400">OT (hrs)</TableHead>
                <TableHead className="text-gray-400">Late</TableHead>
                <TableHead className="text-gray-400">Source</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-8">No attendance records. Download template, fill & upload.</TableCell></TableRow>
              ) : records.map(r => (
                <TableRow key={r.id} className="border-gray-800">
                  <TableCell className="text-gray-300 whitespace-nowrap">{r.date}</TableCell>
                  <TableCell className="text-white font-medium">{r.employee_name}</TableCell>
                  <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${statusBadge(r.status)}`}>{r.status}</span></TableCell>
                  <TableCell className="text-gray-300 font-mono">{r.check_in || "—"}</TableCell>
                  <TableCell className="text-gray-300 font-mono">{r.check_out || "—"}</TableCell>
                  <TableCell className="text-cyan-400 font-mono">{r.overtime_hours > 0 ? r.overtime_hours.toFixed(1) : "—"}</TableCell>
                  <TableCell>{r.late_arrival ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <span className="text-gray-600">—</span>}</TableCell>
                  <TableCell><span className={`text-xs ${r.source === "timesheet" ? "text-cyan-400" : r.source === "csv_upload" ? "text-purple-400" : "text-gray-400"}`}>{r.source}</span></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white h-8 px-2" onClick={() => { setEditing(r); setForm({ employee_name: r.employee_name, date: r.date, status: r.status, check_in: r.check_in || "", check_out: r.check_out || "", notes: r.notes || "" }); setShowDialog(true); }}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 px-2" onClick={() => handleDelete(r.id)}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader><DialogTitle className="text-teal-400">{editing ? "Edit" : "Mark"} Attendance</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-300">Employee</Label>
              <Select value={form.employee_name} onValueChange={v => setForm({...form, employee_name: v})} disabled={!!editing}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue placeholder="Select employee..." /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600 max-h-60">{employees.map(e => <SelectItem key={e.emp_id || e.name} value={e.name} className="text-white">{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-gray-300">Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1 [color-scheme:dark]" disabled={!!editing} /></div>
              <div>
                <Label className="text-gray-300">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">{STATUSES.map(s => <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-gray-300">Check In</Label><Input type="time" value={form.check_in} onChange={e => setForm({...form, check_in: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1 [color-scheme:dark]" /></div>
              <div><Label className="text-gray-300">Check Out</Label><Input type="time" value={form.check_out} onChange={e => setForm({...form, check_out: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1 [color-scheme:dark]" /></div>
            </div>
            <div><Label className="text-gray-300">Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-gray-800 border-gray-600 text-white mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEdit} onOpenChange={v => { if (!v) { setShowBulkEdit(false); setBulkRows([]); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              <List className="w-5 h-5" /> Bulk Edit Attendance
            </DialogTitle>
          </DialogHeader>

          {/* Mode selector */}
          <div className="flex gap-4 items-end">
            <div className="flex gap-2">
              <Button size="sm" variant={bulkMode === "date" ? "default" : "outline"}
                onClick={() => { setBulkMode("date"); setBulkRows([]); }}
                className={bulkMode === "date" ? "bg-amber-700" : "border-gray-600 text-gray-400"}>On Date</Button>
              <Button size="sm" variant={bulkMode === "employee" ? "default" : "outline"}
                onClick={() => { setBulkMode("employee"); setBulkRows([]); }}
                className={bulkMode === "employee" ? "bg-amber-700" : "border-gray-600 text-gray-400"}>Selected Employee</Button>
            </div>
            {bulkMode === "date" && (
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-gray-400 text-xs">Date</Label>
                  <Input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} className="bg-gray-800 border-gray-600 text-white h-9 w-40 [color-scheme:dark]" />
                </div>
                <Button size="sm" onClick={() => openBulkEdit("date")} className="bg-amber-600 hover:bg-amber-700 h-9">Load</Button>
              </div>
            )}
            {bulkMode === "employee" && (
              <div className="flex gap-2 items-end">
                <div>
                  <Label className="text-gray-400 text-xs">Employee</Label>
                  <Select value={bulkEmployee} onValueChange={setBulkEmployee}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-48 h-9"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600 max-h-60">{employees.map(e => <SelectItem key={e.name} value={e.name} className="text-white">{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button size="sm" onClick={() => openBulkEdit("employee")} className="bg-amber-600 hover:bg-amber-700 h-9" disabled={!bulkEmployee}>Load</Button>
              </div>
            )}
          </div>

          {/* Editable grid */}
          {bulkRows.length > 0 && (
            <>
              <div className="flex-1 overflow-auto border border-gray-700 rounded mt-2">
                <table className="w-full text-xs">
                  <thead className="bg-gray-800 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-gray-400 w-8">#</th>
                      {bulkMode === "date" && <th className="px-2 py-1.5 text-left text-gray-400">Employee</th>}
                      <th className="px-2 py-1.5 text-left text-gray-400">Date</th>
                      <th className="px-2 py-1.5 text-left text-gray-400">Status</th>
                      <th className="px-2 py-1.5 text-left text-gray-400">Check In</th>
                      <th className="px-2 py-1.5 text-left text-gray-400">Check Out</th>
                      <th className="px-2 py-1.5 text-left text-gray-400">OT Hours</th>
                      <th className="px-2 py-1.5 text-left text-gray-400">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((row, idx) => (
                      <tr key={idx} className="border-t border-gray-800 hover:bg-gray-800/30">
                        <td className="px-2 py-1 text-gray-600">{idx + 1}</td>
                        {bulkMode === "date" && <td className="px-2 py-1 text-white font-medium text-xs">{row.employee_name}</td>}
                        <td className="px-2 py-1 text-gray-300 text-xs whitespace-nowrap">{row.date}</td>
                        <td className="px-1 py-1">
                          <select value={row.status} onChange={e => updateBulkRow(idx, "status", e.target.value)} className="bg-gray-800 border border-gray-700 text-white text-xs rounded px-1 py-0.5 w-20">
                            <option value="">—</option>
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="px-1 py-1"><input type="time" value={row.check_in} onChange={e => updateBulkRow(idx, "check_in", e.target.value)} className="bg-transparent border-b border-gray-700 text-white text-xs px-1 py-0.5 outline-none [color-scheme:dark] w-20" /></td>
                        <td className="px-1 py-1"><input type="time" value={row.check_out} onChange={e => updateBulkRow(idx, "check_out", e.target.value)} className="bg-transparent border-b border-gray-700 text-white text-xs px-1 py-0.5 outline-none [color-scheme:dark] w-20" /></td>
                        <td className="px-1 py-1"><input type="number" step="0.5" value={row.overtime_hours} onChange={e => updateBulkRow(idx, "overtime_hours", e.target.value)} className="bg-transparent border-b border-gray-700 text-cyan-400 text-xs px-1 py-0.5 outline-none w-14" /></td>
                        <td className="px-1 py-1"><input value={row.notes} onChange={e => updateBulkRow(idx, "notes", e.target.value)} className="w-full bg-transparent border-b border-gray-700 text-white text-xs px-1 py-0.5 outline-none" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" onClick={() => { setShowBulkEdit(false); setBulkRows([]); }} className="border-gray-600 text-gray-300">Cancel</Button>
                <Button onClick={handleBulkSave} disabled={savingBulk} className="bg-amber-600 hover:bg-amber-700">
                  {savingBulk ? "Saving..." : `Save ${bulkRows.filter(r => r.status).length} Entries`}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRAttendance;
